import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, SimpleGrid, Spinner,
  Badge, Icon, Divider, Alert, AlertIcon, Code,
} from '@chakra-ui/react';
import { FiRefreshCw, FiCheckCircle, FiXCircle, FiAlertTriangle, FiDatabase, FiServer, FiSend } from 'react-icons/fi';

const API_BASE = '/api/v1';

function HealthCheck() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchHealth = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/health/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => { setHealth(data); setLastChecked(new Date()); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const statusColor = (status) => {
    switch (status) {
      case 'healthy': case 'connected': case 'complete': return 'green';
      case 'degraded': case 'incomplete': return 'orange';
      case 'not_configured': return 'yellow';
      case 'error': case 'unhealthy': return 'red';
      default: return 'gray';
    }
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'healthy': case 'connected': case 'complete': return FiCheckCircle;
      case 'degraded': case 'incomplete': case 'not_configured': return FiAlertTriangle;
      case 'error': case 'unhealthy': return FiXCircle;
      default: return FiAlertTriangle;
    }
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="purple.500" /></Box>;
  if (!health) return <Alert status="error"><AlertIcon />Failed to load health check data</Alert>;

  const checks = health.checks || {};

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Health Check</Heading>
          <Text color="gray.500">
            Service connectivity and status
            {lastChecked && ` — last checked ${lastChecked.toLocaleTimeString()}`}
          </Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="purple" onClick={fetchHealth} isLoading={loading}>Re-check</Button>
      </HStack>

      <Alert
        status={health.overall === 'healthy' ? 'success' : health.overall === 'degraded' ? 'warning' : 'error'}
        borderRadius="lg" mb={6} py={4}
      >
        <AlertIcon boxSize={6} />
        <VStack align="start" spacing={0} ml={2}>
          <Text fontWeight="bold" fontSize="lg">
            Overall: <Badge colorScheme={statusColor(health.overall)} fontSize="md" px={2}>{health.overall}</Badge>
          </Text>
        </VStack>
      </Alert>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        {/* Database */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiDatabase} boxSize={6} color="blue.500" />
            <Heading size="md">Database</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.database?.status)} fontSize="sm">{checks.database?.status || 'unknown'}</Badge>
            </HStack>
            {checks.database?.latency_ms != null && (
              <HStack justify="space-between">
                <Text color="gray.600">Latency</Text>
                <Text fontWeight="bold">{checks.database.latency_ms} ms</Text>
              </HStack>
            )}
            {checks.database?.message && (
              <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{checks.database.message}</Alert>
            )}
          </VStack>
        </Box>

        {/* Instances */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiServer} boxSize={6} color="purple.500" />
            <Heading size="md">Instances</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.instances?.status)} fontSize="sm">{checks.instances?.status || 'unknown'}</Badge>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Total</Text>
              <Text fontWeight="bold">{checks.instances?.total || 0}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Full</Text>
              <Text fontWeight="bold">{checks.instances?.full || 0}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Lite</Text>
              <Text fontWeight="bold">{checks.instances?.lite || 0}</Text>
            </HStack>
            {checks.instances?.message && (
              <Alert status="warning" borderRadius="md" fontSize="sm"><AlertIcon />{checks.instances.message}</Alert>
            )}
          </VStack>
        </Box>

        {/* Webhooks */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiSend} boxSize={6} color="orange.500" />
            <Heading size="md">Webhooks</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.webhooks?.status)} fontSize="sm">{checks.webhooks?.status || 'unknown'}</Badge>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Total</Text>
              <Text fontWeight="bold">{checks.webhooks?.total || 0}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Active</Text>
              <Text fontWeight="bold">{checks.webhooks?.active || 0}</Text>
            </HStack>
            {checks.webhooks?.message && (
              <Alert status="warning" borderRadius="md" fontSize="sm"><AlertIcon />{checks.webhooks.message}</Alert>
            )}
          </VStack>
        </Box>
      </SimpleGrid>
    </Box>
  );
}

export default HealthCheck;
